
import { useState } from "react";
import { X, UploadCloud } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

interface AttachmentUploadProps {
  attachmentFile: File | null;
  setAttachmentFile: (file: File | null) => void;
  attachmentPreview: string | null;
  setAttachmentPreview: (preview: string | null) => void;
}

export function AttachmentUpload({
  attachmentFile,
  setAttachmentFile,
  attachmentPreview,
  setAttachmentPreview,
}: AttachmentUploadProps) {
  const [isDragging, setIsDragging] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    
    if (file) {
      setAttachmentFile(file);
      
      // Create preview URL for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setAttachmentPreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setAttachmentPreview(null);
      }
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const file = e.dataTransfer.files?.[0] || null;
    if (file) {
      setAttachmentFile(file);
      
      // Create preview URL for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setAttachmentPreview(e.target?.result as string);
        };
        reader.readAsDataURL(file);
      } else {
        setAttachmentPreview(null);
      }
    }
  };

  const removeAttachment = () => {
    setAttachmentFile(null);
    setAttachmentPreview(null);
  };

  return (
    <div className="space-y-2">
      {!attachmentFile ? (
        <div
          className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${
            isDragging 
              ? "border-primary bg-primary/5" 
              : "border-stone-300 hover:border-primary/40 hover:bg-stone-50"
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => document.getElementById("file-upload")?.click()}
        >
          <UploadCloud className="mx-auto h-12 w-12 text-stone-400" />
          <p className="mt-2 text-sm text-stone-600">
            Drag and drop or click to upload an image or screenshot
          </p>
          <p className="text-xs text-stone-500 mt-1">
            Supports: PNG, JPG, JPEG, GIF (max 5MB)
          </p>
          <Input
            id="file-upload"
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      ) : (
        <div className="relative rounded-md border border-stone-200 p-2">
          <div className="flex items-center space-x-3">
            {attachmentPreview ? (
              <img
                src={attachmentPreview}
                alt="Attachment preview"
                className="h-16 w-16 rounded object-cover"
              />
            ) : (
              <div className="h-16 w-16 rounded bg-stone-100 flex items-center justify-center">
                <span className="text-xs text-stone-500">File</span>
              </div>
            )}
            <div className="flex-1 truncate">
              <p className="text-sm font-medium text-stone-700 truncate">
                {attachmentFile.name}
              </p>
              <p className="text-xs text-stone-500">
                {(attachmentFile.size / 1024).toFixed(1)} KB
              </p>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 w-8 p-0"
              onClick={removeAttachment}
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Remove</span>
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
